/**
 * Gmail Service - Handle OAuth & Email Operations
 * 
 * Features:
 * - OAuth2 authentication with Google
 * - Send emails via Gmail API
 * - Fetch inbox emails
 * - Refresh tokens
 * - Disconnect/revoke access
 */

import { supabase } from '../lib/supabase';

class GmailService {
  constructor() {
    this.clientId = import.meta.env.VITE_GMAIL_CLIENT_ID;
    this.redirectUri = import.meta.env.VITE_GMAIL_REDIRECT_URI;
    this.scopes = [
      'https://www.googleapis.com/auth/gmail.send',
      'https://www.googleapis.com/auth/gmail.readonly',
      'https://www.googleapis.com/auth/gmail.modify'
    ];
  }

  /**
   * Get OAuth authorization URL
   * User will be redirected to Google login
   */
  getAuthUrl() {
    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.redirectUri,
      response_type: 'code',
      scope: this.scopes.join(' '),
      access_type: 'offline',
      prompt: 'consent' // Force consent to get refresh token
    });
    return `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  }

  /**
   * Handle OAuth callback - exchange code for tokens
   * @param {string} code - Authorization code from Google
   * @returns {Promise} User Gmail tokens saved
   */
  async handleAuthCallback(code) {
    try {
      if (!code) throw new Error('No authorization code provided');

      // Call backend edge function to exchange code for tokens
      // (We can't call Google API directly from client - need backend)
      const response = await fetch('/api/gmail/exchange-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });

      if (!response.ok) {
        throw new Error(`Failed to exchange code: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Tokens now stored in ugc_gmail_tokens table by backend
      return {
        success: true,
        gmailEmail: data.email,
        message: 'Gmail connected successfully!'
      };
    } catch (error) {
      console.error('[Gmail Auth Callback Error]:', error);
      throw error;
    }
  }

  /**
   * Send email via Gmail API
   * @param {Object} options - Email options
   * @returns {Promise} Email sent result
   */
  async sendEmail({ to, subject, body, cc, bcc }) {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('Not authenticated');

      // Call backend to send email
      const response = await fetch('/api/gmail/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to,
          subject,
          body,
          cc,
          bcc
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to send email: ${response.statusText}`);
      }

      const result = await response.json();
      
      // Log email to database
      await this.logEmailSent({
        to,
        subject,
        body,
        gmailMessageId: result.messageId
      });

      return {
        success: true,
        messageId: result.messageId,
        message: 'Email sent successfully'
      };
    } catch (error) {
      console.error('[Gmail Send Error]:', error);
      throw error;
    }
  }

  /**
   * Get Gmail connection status
   * @returns {Promise<Object>} Connection info
   */
  async getConnectionStatus() {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) return { connected: false };

      const { data, error } = await supabase
        .from('ugc_gmail_tokens')
        .select('gmail_email, created_at, last_synced_at')
        .eq('user_id', user.id)
        .single();

      if (error || !data) {
        return { connected: false };
      }

      // Check if token is expired
      const expiresAt = new Date(data.token_expires_at);
      const isExpired = expiresAt < new Date();

      return {
        connected: true,
        gmailEmail: data.gmail_email,
        connectedSince: data.created_at,
        lastSynced: data.last_synced_at,
        tokenExpired: isExpired
      };
    } catch (error) {
      console.error('[Gmail Status Check Error]:', error);
      return { connected: false, error: error.message };
    }
  }

  /**
   * Fetch recent inbox emails
   * @param {number} limit - Number of emails to fetch
   * @returns {Promise} Array of emails
   */
  async getInboxEmails(limit = 10) {
    try {
      // Call backend to fetch from Gmail API
      const response = await fetch('/api/gmail/get-inbox', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch inbox: ${response.statusText}`);
      }

      const emails = await response.json();
      
      // Store received emails in database
      for (const email of emails.slice(0, limit)) {
        await this.logEmailReceived(email);
      }

      return emails.slice(0, limit);
    } catch (error) {
      console.error('[Gmail Inbox Error]:', error);
      throw error;
    }
  }

  /**
   * Disconnect Gmail (revoke access token)
   * @returns {Promise} Disconnection result
   */
  async disconnect() {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) throw new Error('Not authenticated');

      // Call backend to revoke token
      await fetch('/api/gmail/disconnect', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      // Delete token from database
      const { error } = await supabase
        .from('ugc_gmail_tokens')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;

      return { success: true, message: 'Gmail disconnected' };
    } catch (error) {
      console.error('[Gmail Disconnect Error]:', error);
      throw error;
    }
  }

  /**
   * Log sent email to database
   * @private
   */
  async logEmailSent({ to, subject, body, gmailMessageId }) {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) return;

      // Find contact by email
      const { data: contact } = await supabase
        .from('ugc_contacts')
        .select('id')
        .eq('user_id', user.id)
        .eq('email', to)
        .single();

      await supabase
        .from('ugc_emails')
        .insert({
          user_id: user.id,
          contact_id: contact?.id || null,
          subject,
          body,
          direction: 'sent',
          status: 'sent',
          gmail_message_id: gmailMessageId,
          sent_at: new Date().toISOString()
        });
    } catch (error) {
      console.error('[Log Email Sent Error]:', error);
    }
  }

  /**
   * Log received email to database
   * @private
   */
  async logEmailReceived(email) {
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) return;

      // Find contact by email
      const { data: contact } = await supabase
        .from('ugc_contacts')
        .select('id')
        .eq('user_id', user.id)
        .eq('email', email.from)
        .single();

      await supabase
        .from('ugc_emails')
        .insert({
          user_id: user.id,
          contact_id: contact?.id || null,
          subject: email.subject,
          body: email.body,
          direction: 'received',
          status: 'delivered',
          gmail_message_id: email.messageId,
          received_at: new Date(email.date).toISOString()
        });
    } catch (error) {
      console.error('[Log Email Received Error]:', error);
    }
  }
}

export const gmailService = new GmailService();
